import { clone, isEmpty, isFunction, pluck } from 'underscore';
import React, { useCallback, useEffect, useMemo, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  useId,
  Combobox,
  Option,
} from '@fluentui/react-components';
import { useDebouncedState, usePrevProp } from '@bingads-webui-campaign-react/common-generic-hooks';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InlineLoading, LoadingOverlay } from '@bingads-webui-campaign-react/loading';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';
import {
  useMultiSelectContext,
  withMultiSelectCombobox,
  useFluentV1Styles,
} from '@bingads-webui-campaign-react/multi-selector';
import { OptionItem } from './option-item';
import { MaxSelectionAlert, MoreOptionsInlineAlert, NoDataInlineAlert, NoInitialDataAlert, SystemErrorAlert } from './alerts';

const MultiSelectCombobox = withMultiSelectCombobox(Combobox);

const usePageFeedSelectorSectionStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('10px'),
  },
  rootLoading: {
    minHeight: '64px',
  },
  description: {
    marginBottom: tokens.spacingVerticalS,
  },
  tagsList: {
    marginBottom: tokens.spacingVerticalXS,
    marginTop: 0,
    paddingLeft: 0,
    display: 'flex',
    gridGap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  comboAndSpinner: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: tokens.spacingHorizontalM,
  },
  combobox: {
    width: '560px',
  },
  listbox: {
    maxHeight: '245px',
  },
  checkIcon: {
    flexShrink: 0,
  },
});

export const PageFeedSelectorSectionImpl = ({
  onChange: onChangeFromProp,
  searchStartDelayMs,
  customizedDescription,
  customizedSearchPlaceholder,
}) => {
  const i18n = useContext(I18nContext);
  const comboboxId = useId('combobox');
  const styles = usePageFeedSelectorSectionStyles();
  const fluentV1ComboboxStyles = useFluentV1Styles();

  const {
    isLoadingOptions,
    isFirstOptionsLoad,
    isNoOptionsAfterFirstLoad,
    isMoreOptionsThanLimit,
    options,
    optionsLoadError,
    lastLoadedSearchFilter,
    optionsDisplayLimit,
    selectedOptionsLimit,

    isLoadingSelectedOptions,
    isSelectedOptionsLimitReached,
    selectedOptions,
    selectedOptionsLoadError,
    setSelectedOptions,

    setSearchFilter: setSearchFilterFromContext,
  } = useMultiSelectContext();

  const [debouncedSearchFilter, setSearchFilter, searchFilter] = useDebouncedState('', searchStartDelayMs);
  useEffect(() => {
    setSearchFilterFromContext(debouncedSearchFilter);
  }, [debouncedSearchFilter, setSearchFilterFromContext]);

  const onChange = useCallback((event) => {
    const { value: rawValue } = event.target;
    setSearchFilter(rawValue);
  }, [setSearchFilter]);

  const onOptionSelect = useCallback((event, data) => {
    setSelectedOptions(data.resolvedSelectedOptions);
    if (isFunction(onChangeFromProp)) {
      onChangeFromProp(data.resolvedSelectedOptions);
    }
  }, [setSelectedOptions, onChangeFromProp]);

  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpenStateChangedByUser, setIsDropdownOpenStateChangedByUser] = useState(false);
  const onOpenChange = useCallback((event, data) => {
    setIsOpen(data.open);
    setIsDropdownOpenStateChangedByUser(true);
  }, []);

  const onDeleteItem = useCallback((index) => {
    const newSelectedOptions = clone(selectedOptions);
    newSelectedOptions.splice(index, 1);
    setSelectedOptions(newSelectedOptions);
    if (isFunction(onChangeFromProp)) {
      onChangeFromProp(newSelectedOptions);
    }
  }, [selectedOptions, setSelectedOptions, onChangeFromProp]);

  const selectedOptionKeys = useMemo(() => pluck(selectedOptions, 'key'), [selectedOptions]);
  const selectedOptionsSet = useMemo(() => new Set(selectedOptionKeys), [selectedOptionKeys]);
  const shouldDisableSelection = useCallback(option => isSelectedOptionsLimitReached && !selectedOptionsSet.has(option.key), [isSelectedOptionsLimitReached, selectedOptionsSet]);

  const isAnyLoading = isLoadingOptions || isLoadingSelectedOptions;
  const hasLoadErrors = optionsLoadError || selectedOptionsLoadError;
  const showInitialLoading = (isFirstOptionsLoad && !hasLoadErrors) || isLoadingSelectedOptions;
  const hasInitialLoadErrors = isFirstOptionsLoad && hasLoadErrors;
  const canUserStartOperating = !showInitialLoading && !isNoOptionsAfterFirstLoad && !(hasInitialLoadErrors || selectedOptionsLoadError);

  const [, hasIsLoadingOptionsChanged] = usePrevProp(isLoadingOptions);
  const [, hasIsLoadingSelectedOptionsChanged] = usePrevProp(isLoadingSelectedOptions);
  if ((hasIsLoadingOptionsChanged && isLoadingOptions) ||
    (hasIsLoadingSelectedOptionsChanged && isLoadingSelectedOptions)) {
    setIsDropdownOpenStateChangedByUser(false);
  }

  const [, hasOptionsLoadErrorChanged] = usePrevProp(optionsLoadError);
  const [, hasSelectedOptionsLoadErrorChanged] = usePrevProp(selectedOptionsLoadError);
  if (((hasOptionsLoadErrorChanged && optionsLoadError) ||
    (hasSelectedOptionsLoadErrorChanged && selectedOptionsLoadError)) && !isDropdownOpenStateChangedByUser) {
    // Close the dropdown if there is an error and the user didn't change the dropdown after the loading started
    setIsOpen(false);
  }

  // Rendering
  return (
    <div className={mergeClasses('page-feed-selector', styles.root, showInitialLoading && styles.rootLoading)}>
      <span className={mergeClasses('page-feed-selector-description', styles.description)}>{customizedDescription || i18n.getString(_TL_('Add your page feeds below.'))}</span>
      {!showInitialLoading && isNoOptionsAfterFirstLoad &&
        <NoInitialDataAlert />
      }
      {!isAnyLoading && hasLoadErrors &&
        <SystemErrorAlert />
      }
      {canUserStartOperating &&
        <div className={mergeClasses('page-feed-selector-selected-items', styles.tagsList)}>
          {selectedOptions.map((item, index) => (
            <SelectedItem
              key={item.key}
              itemText={item.text}
              itemIndex={index}
              onDelete={onDeleteItem}
            />
          ))}
        </div>
      }
      {!showInitialLoading && isSelectedOptionsLimitReached &&
        <MaxSelectionAlert selectedOptionsLimit={selectedOptionsLimit} />
      }

      <div className={styles.comboAndSpinner}>
        {canUserStartOperating &&
          <MultiSelectCombobox
            id={comboboxId}
            placeholder={customizedSearchPlaceholder || i18n.getString(_TL_('Choose page feeds'))}
            listbox={{ className: styles.listbox }}
            root={{ className: mergeClasses(styles.combobox, fluentV1ComboboxStyles.root) }}
            disabled={!canUserStartOperating}
            onOptionSelect={onOptionSelect}
            onChange={onChange}
            value={searchFilter}
            options={options}
            selectedOptions={selectedOptions}
            open={isOpen}
            onOpenChange={onOpenChange}
          >
            {!optionsLoadError &&
              <>
                {isEmpty(options) &&
                  <LoadingOverlay className="loading-overlay-no-data-alert" isVisible={isLoadingOptions} flexDirection="column">
                    <NoDataInlineAlert searchFilter={lastLoadedSearchFilter} />
                  </LoadingOverlay>
                }
                {options.map(option => (
                  <Option key={option.key} value={option.key} text={option.text} disabled={isLoadingOptions || shouldDisableSelection(option)} checkIcon={{ className: styles.checkIcon }} >
                    <OptionItem option={option} />
                  </Option>
                ))}
                {isMoreOptionsThanLimit &&
                  <LoadingOverlay className="loading-overlay-more-options-alert" isVisible={isLoadingOptions} flexDirection="column">
                    <MoreOptionsInlineAlert optionsLimit={optionsDisplayLimit} />
                  </LoadingOverlay>
                }
              </>
            }
            {optionsLoadError && <span aria-errormessage={i18n.getString('No items to display due to a system error')}>&nbsp;</span>}
          </MultiSelectCombobox>
        }
        {isAnyLoading && <InlineLoading text={i18n.getString(_TL_('Loading...'))} />}
      </div>
    </div>
  );
};

PageFeedSelectorSectionImpl.propTypes = {
  onChange: PropTypes.func,
  searchStartDelayMs: PropTypes.number.isRequired,
  customizedDescription: PropTypes.string,
  customizedSearchPlaceholder: PropTypes.string,
};

PageFeedSelectorSectionImpl.defaultProps = {
  onChange: undefined,
  customizedDescription: undefined,
  customizedSearchPlaceholder: undefined,
};
